/**
 * Nova's rules engine — pure functions over CRM state.
 *
 * Each rule is a function `(input) => NovaSuggestion[]`. They're pure
 * (no fetching, no side effects) so they're trivial to test and to
 * port to Swift line-for-line.
 *
 * Add a new rule:
 *   1. Write a function below that returns NovaSuggestion[].
 *   2. Add it to the `RULES` array at the bottom.
 *   3. The engine wires it into useNovaSuggestions automatically.
 */

import type {
  NovaSuggestion,
  NovaSuggestionPriority,
} from '@/types/novaSuggestion';
import { PRIORITY_WEIGHT } from '@/types/novaSuggestion';

// Loose row shapes — we accept whatever the supabase Tables types
// give us without forcing the rules to know about every column.
export interface RuleInput {
  clients: ClientRow[];
  projects: ProjectRow[];
  inquiries: InquiryRow[];
  deliverables: DeliverableRow[];
  goals?: GoalRow[];
  /** Now reference — pass in for testability; defaults to wall clock. */
  now?: Date;
}

export interface GoalRow {
  id: string;
  title: string;
  kind: 'revenue' | 'projects' | 'clients' | 'shoots' | 'custom' | string;
  target_value: number | null;
  target_date: string | null;
  progress_value: number;
  status: 'active' | 'achieved' | 'archived' | string;
}

export interface ClientRow {
  id: string;
  name: string;
  created_at?: string | null;
}

export interface ProjectRow {
  id: string;
  name: string | null;
  status: string | null;
  client_id: string | null;
  shoot_date?: string | null;
  delivery_date?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  client?: { id?: string | null; name?: string | null } | null;
}

export interface InquiryRow {
  id: string;
  name: string | null;
  email: string | null;
  status: string | null;
  submittedAt?: string | null;     // some hooks normalize to this
  submitted_at?: string | null;    // raw column name
  last_contacted_at?: string | null;
}

export interface DeliverableRow {
  id: string;
  project_id: string;
  type: string | null;
  approved: boolean | null;
  created_at: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Time helpers
// ─────────────────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

function daysBetween(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / DAY_MS);
}

function parseDate(s?: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function inquirySubmittedAt(i: InquiryRow): Date | null {
  return parseDate(i.submittedAt) ?? parseDate(i.submitted_at);
}

// ─────────────────────────────────────────────────────────────────────────────
// Rules
// ─────────────────────────────────────────────────────────────────────────────

/** Inquiry sitting in 'pending' / 'follow_up' for 7+ days with no contact. */
function ruleIdleInquiries(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const idle = input.inquiries.filter((i) => {
    if (i.status !== 'pending' && i.status !== 'follow_up') return false;
    const lastContact = parseDate(i.last_contacted_at) ?? inquirySubmittedAt(i);
    if (!lastContact) return false;
    return daysBetween(now, lastContact) >= 7;
  });
  if (idle.length === 0) return [];

  // One aggregate suggestion if multiple, else specific one
  if (idle.length === 1) {
    const i = idle[0];
    return [{
      id: `idle_inquiry:${i.id}`,
      kind: 'idle_inquiry',
      title: `${i.name ?? 'A lead'} has been waiting 7+ days`,
      body: `No follow-up since their inquiry. Want me to draft a check-in?`,
      priority: 'high',
      observedAt: (inquirySubmittedAt(i) ?? now).toISOString(),
      inquiryId: i.id,
      action: { label: 'Draft follow-up', href: `/inquiries?id=${i.id}` },
      novaState: 'has-suggestion',
    }];
  }
  return [{
    id: `idle_inquiry:multi:${idle.length}`,
    kind: 'idle_inquiry',
    title: `${idle.length} inquiries idle 7+ days`,
    body: 'They reached out and went quiet. Want me to draft follow-ups?',
    priority: 'high',
    observedAt: now.toISOString(),
    action: { label: 'Review inquiries', href: '/inquiries' },
    novaState: 'has-suggestion',
  }];
}

/** A brand-new inquiry within the last hour. */
function ruleFreshLeads(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const fresh = input.inquiries.filter((i) => {
    if (i.status !== 'pending') return false;
    const sub = inquirySubmittedAt(i);
    if (!sub) return false;
    return now.getTime() - sub.getTime() < 60 * 60 * 1000; // 1 hour
  });
  if (fresh.length === 0) return [];
  const i = fresh[0];
  return [{
    id: `fresh_lead:${i.id}`,
    kind: 'fresh_lead',
    title: `New inquiry from ${i.name ?? 'someone'}`,
    body: 'Just came in. Want to take a look while it\'s warm?',
    priority: 'urgent',
    observedAt: (inquirySubmittedAt(i) ?? now).toISOString(),
    inquiryId: i.id,
    action: { label: 'Open inquiry', href: `/inquiries?id=${i.id}` },
    novaState: 'celebrating',
  }];
}

/** A scheduled shoot landing in the next 0–3 days. */
function ruleUpcomingShoot(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const soon: { p: ProjectRow; date: Date; days: number }[] = [];
  for (const p of input.projects) {
    const date = parseDate(p.shoot_date);
    if (!date) continue;
    const days = daysBetween(date, now);
    if (days >= 0 && days <= 3) soon.push({ p, date, days });
  }
  if (soon.length === 0) return [];
  soon.sort((a, b) => a.date.getTime() - b.date.getTime());
  const { p, days } = soon[0];
  const when = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
  const name = p.client?.name ?? p.name ?? 'Your shoot';
  return [{
    id: `upcoming_shoot:${p.id}`,
    kind: 'upcoming_shoot',
    title: `${name} is ${when}`,
    body: 'Want me to pull the shot list, venue notes, and gear pack?',
    priority: days <= 1 ? 'high' : 'medium',
    observedAt: now.toISOString(),
    projectId: p.id,
    action: { label: 'Open project', href: `/studio/${p.id}` },
    novaState: 'curious',
  }];
}

/** Project stuck in 'editing' for 14+ days since last update. */
function ruleStalledProject(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const stalled = input.projects.filter((p) => {
    if (p.status !== 'editing') return false;
    const updated = parseDate(p.updated_at) ?? parseDate(p.created_at);
    if (!updated) return false;
    return daysBetween(now, updated) >= 14;
  });
  if (stalled.length === 0) return [];
  const p = stalled[0];
  const name = p.client?.name ?? p.name ?? 'A project';
  return [{
    id: `stalled_project:${p.id}`,
    kind: 'stalled_project',
    title: `${name} has been in editing for 2+ weeks`,
    body: 'No updates lately. Want me to ping you with a deadline check?',
    priority: 'medium',
    observedAt: now.toISOString(),
    projectId: p.id,
    action: { label: 'Open project', href: `/studio/${p.id}` },
    novaState: 'cross',
  }];
}

/** No shoots scheduled in the next 7 days — gentle marketing nudge. */
function ruleQuietWeek(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const inAWeek = new Date(now.getTime() + 7 * DAY_MS);
  const upcomingThisWeek = input.projects.filter((p) => {
    const date = parseDate(p.shoot_date);
    return date && date >= now && date <= inAWeek;
  });
  if (upcomingThisWeek.length > 0) return [];
  // Only fire if they have at least one client (so it's not redundant
  // with the no_clients_yet onboarding nudge).
  if (input.clients.length === 0) return [];
  return [{
    id: 'quiet_week:current',
    kind: 'quiet_week',
    title: 'Quiet week ahead',
    body: 'No shoots scheduled in the next 7 days. Want me to draft a "this month\'s availability" post?',
    priority: 'low',
    observedAt: now.toISOString(),
    action: { label: 'Draft post', href: '/assistant' },
    novaState: 'curious',
  }];
}

/** Deliverable uploaded 5+ days ago, still not approved by client. */
function ruleFreshDeliveryAwaiting(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const stale = input.deliverables.filter((d) => {
    if (d.approved) return false;
    const created = parseDate(d.created_at);
    if (!created) return false;
    const days = daysBetween(now, created);
    return days >= 5 && days <= 30;
  });
  if (stale.length === 0) return [];
  // Group by project and surface the project with the most pending
  const byProject = new Map<string, number>();
  for (const d of stale) {
    byProject.set(d.project_id, (byProject.get(d.project_id) ?? 0) + 1);
  }
  const [pid, count] = [...byProject.entries()].sort((a, b) => b[1] - a[1])[0];
  const project = input.projects.find((p) => p.id === pid);
  const name = project?.client?.name ?? project?.name ?? 'A delivery';
  return [{
    id: `fresh_delivery:${pid}`,
    kind: 'fresh_delivery',
    title: `${name} hasn\'t reviewed your delivery`,
    body: `${count} file${count === 1 ? '' : 's'} sent ${5}+ days ago. Want me to send a friendly nudge?`,
    priority: 'medium',
    observedAt: now.toISOString(),
    projectId: pid,
    // P2-1 — used to deep-link the legacy /client/:projectId page.
    // The studio-side home for a delivery nudge is the project
    // workspace, which carries the real portal controls + share link.
    action: { label: 'Open project', href: `/studio/${pid}` },
    novaState: 'has-suggestion',
  }];
}

/** Delivered project sitting 10+ days — opportunity for upsell / testimonial. */
function ruleDeliveryFollowup(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const ripe = input.projects.filter((p) => {
    if (p.status !== 'delivered') return false;
    const updated = parseDate(p.updated_at) ?? parseDate(p.created_at);
    if (!updated) return false;
    const days = daysBetween(now, updated);
    return days >= 10 && days <= 45;
  });
  if (ripe.length === 0) return [];
  const p = ripe[0];
  const name = p.client?.name ?? p.name ?? 'A delivered project';
  return [{
    id: `delivery_followup:${p.id}`,
    kind: 'delivery_followup',
    title: `${name} delivered 10+ days ago`,
    body: 'Good moment for a testimonial ask or album upsell. Want me to draft one?',
    priority: 'medium',
    observedAt: now.toISOString(),
    projectId: p.id,
    action: { label: 'Draft message', href: `/studio/${p.id}` },
    novaState: 'happy',
  }];
}

/** Studio with zero clients — onboarding nudge. */
function ruleNoClientsYet(input: RuleInput): NovaSuggestion[] {
  if (input.clients.length > 0) return [];
  return [{
    id: 'no_clients_yet',
    kind: 'no_clients_yet',
    title: 'Add your first client',
    body: 'Once you have a client, I can start watching their project and surface things you might miss.',
    priority: 'medium',
    observedAt: (input.now ?? new Date()).toISOString(),
    action: { label: 'Add client', href: '/clients' },
    novaState: 'happy',
  }];
}

/** Has clients but never made a project — second-step nudge. */
function ruleNoFirstProject(input: RuleInput): NovaSuggestion[] {
  if (input.clients.length === 0) return [];
  if (input.projects.length > 0) return [];
  return [{
    id: 'no_first_project',
    kind: 'no_first_project',
    title: 'Spin up your first project',
    body: 'You have clients — turn one into a project to start tracking shoots, deliverables, and milestones.',
    priority: 'low',
    observedAt: (input.now ?? new Date()).toISOString(),
    action: { label: 'New project', href: '/projects' },
    novaState: 'happy',
  }];
}

// ─────────────────────────────────────────────────────────────────────────────
// Goal-watching rules
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compare expected pace (days elapsed / total days) to actual progress
 * fraction. Fire a NovaSuggestion when actual is meaningfully behind.
 *
 * Phrasing varies by kind so the tip is concrete and actionable.
 */
function ruleGoalAtRisk(input: RuleInput): NovaSuggestion[] {
  const now = input.now ?? new Date();
  const goals = (input.goals ?? []).filter((g) => g.status === 'active');
  if (goals.length === 0) return [];

  const out: NovaSuggestion[] = [];
  for (const g of goals) {
    if (!g.target_value || g.target_value <= 0) continue;
    const fraction = Math.min(1, g.progress_value / g.target_value);

    // Goal already hit (>=100%) — fire a celebrating one-shot
    if (fraction >= 1) {
      out.push({
        id: `goal_hit:${g.id}`,
        kind: 'delivery_followup',
        title: `🎉 ${g.title} — done`,
        body: `You hit your target. Want to set the next one?`,
        priority: 'high',
        observedAt: now.toISOString(),
        action: { label: 'Set next goal', href: '/galaxy?new=1' },
        novaState: 'celebrating',
      });
      continue;
    }

    // No deadline → can't compute pace, skip pace-based at-risk
    if (!g.target_date) continue;
    const deadline = parseDate(g.target_date);
    if (!deadline) continue;
    // Assume goal started at created_at; if missing, use 30 days back
    // (so a fresh goal isn't immediately "behind"). The Goal model
    // doesn't expose created_at on the rule shape, so we approximate
    // by working backward from deadline using a 90-day window.
    const totalWindowMs = 90 * DAY_MS;
    const startEstimate = new Date(deadline.getTime() - totalWindowMs);
    if (now < startEstimate) continue;
    const totalSpan = Math.max(1, deadline.getTime() - startEstimate.getTime());
    const elapsed = Math.max(0, now.getTime() - startEstimate.getTime());
    const expectedFraction = Math.min(1, elapsed / totalSpan);

    // Fire when actual is at least 15 percentage points behind the
    // expected curve — a meaningful gap, not a rounding blip.
    if (expectedFraction - fraction < 0.15) continue;

    const daysLeft = Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / DAY_MS));
    const remaining = Math.max(0, g.target_value - g.progress_value);
    const niceRemaining = g.kind === 'revenue'
      ? `$${Math.round(remaining).toLocaleString()}`
      : `${Math.round(remaining)}`;

    let body = `You're at ${Math.round(fraction * 100)}% with ${daysLeft} day${daysLeft === 1 ? '' : 's'} left. `;
    let action: NovaSuggestionAction;
    switch (g.kind) {
      case 'revenue':
        body += `Need ${niceRemaining} more — want me to draft an upsell email to recent clients?`;
        action = { label: 'Draft upsell', href: '/assistant' };
        break;
      case 'projects':
      case 'shoots':
        body += `${niceRemaining} to go. Want me to suggest a campaign push?`;
        action = { label: 'Plan a push', href: '/assistant' };
        break;
      case 'clients':
        body += `${niceRemaining} more clients needed. Want me to draft a referral ask?`;
        action = { label: 'Draft referral ask', href: '/assistant' };
        break;
      default:
        body += `Want to talk through what's slowing it down?`;
        action = { label: 'Open Nova', href: '/assistant' };
    }

    out.push({
      id: `goal_at_risk:${g.id}`,
      kind: 'stalled_project',
      title: `${g.title} — falling behind`,
      body,
      priority: daysLeft <= 14 ? 'high' : 'medium',
      observedAt: now.toISOString(),
      action,
      novaState: daysLeft <= 7 ? 'cross' : 'has-suggestion',
    });
  }
  return out;
}

// Type alias for the action shape used inline above
type NovaSuggestionAction = NovaSuggestion['action'];

// ─────────────────────────────────────────────────────────────────────────────
// Engine
// ─────────────────────────────────────────────────────────────────────────────

const RULES: ((input: RuleInput) => NovaSuggestion[])[] = [
  ruleFreshLeads,
  ruleIdleInquiries,
  ruleUpcomingShoot,
  ruleStalledProject,
  ruleFreshDeliveryAwaiting,
  ruleDeliveryFollowup,
  ruleGoalAtRisk,
  ruleQuietWeek,
  ruleNoClientsYet,
  ruleNoFirstProject,
];

/**
 * Runs every rule and returns suggestions sorted by priority then
 * recency (newer signals first within the same priority band).
 *
 * Caller decides how many to surface — typically 1 hero suggestion
 * + 2 in a "more" affordance.
 */
export function computeNovaSuggestions(input: RuleInput): NovaSuggestion[] {
  const all: NovaSuggestion[] = [];
  for (const rule of RULES) {
    try {
      const out = rule(input);
      all.push(...out);
    } catch {
      // A bad rule shouldn't kill the whole engine — drop it silently.
    }
  }
  return all.sort((a, b) => {
    const pri = PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
    if (pri !== 0) return pri;
    return new Date(b.observedAt).getTime() - new Date(a.observedAt).getTime();
  });
}
